import { useMemo, useState } from 'react';
import { useCrm } from '../store';
import { Card } from '../ui';
import { Boxes, Plus, Search, Trash2, X } from 'lucide-react';
import type { Produit, PrestationProduit } from '../types';

export type ProduitLigne = Omit<PrestationProduit, 'prestationId'>;

const eur = (n: number) => `${Math.round(Number(n || 0)).toLocaleString('fr-FR')} €`;

export function makeProduitLigne(p: Produit, ordre: number): ProduitLigne {
  return {
    id: `pp-${p.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    produitId: p.id,
    libelle: p.libelle,
    description: p.description || '',
    quantite: 1,
    coutHT: Number(p.coutHT || 0),
    prixHT: Number(p.prixHT || 0),
    ordre,
  };
}

export function produitsTotals(lignes: ProduitLigne[]) {
  const coutHT = lignes.reduce((s, l) => s + (Number(l.coutHT) || 0) * (Number(l.quantite) || 0), 0);
  const venduHT = lignes.reduce((s, l) => s + (Number(l.prixHT) || 0) * (Number(l.quantite) || 0), 0);
  return { coutHT, venduHT, margeHT: venduHT - coutHT };
}

export function ProduitsPicker({
  lignes,
  onChange,
}: {
  lignes: ProduitLigne[];
  onChange: (next: ProduitLigne[]) => void;
}) {
  const data = useCrm();
  const catalogue = ((data.produits || []) as Produit[]).filter((p) => p.actif);
  const categories = (data.produitCategories || []) as string[];
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('');

  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalogue
      .filter((p) => (cat ? p.categorie === cat : true))
      .filter((p) => {
        if (!q) return false;
        return `${p.libelle} ${p.categorie} ${p.description}`.toLowerCase().includes(q);
      })
      .slice(0, 8);
  }, [catalogue, search, cat]);

  const browse = useMemo(
    () => (cat ? catalogue.filter((p) => p.categorie === cat).slice(0, 30) : []),
    [catalogue, cat]
  );

  const add = (p: Produit) => {
    onChange([...lignes, makeProduitLigne(p, lignes.length)]);
    setSearch('');
  };

  const addLibre = () => {
    onChange([
      ...lignes,
      {
        id: `pp-libre-${Date.now()}`,
        produitId: null,
        libelle: '',
        description: '',
        quantite: 1,
        coutHT: 0,
        prixHT: 0,
        ordre: lignes.length,
      },
    ]);
  };

  const patch = (id: string, p: Partial<ProduitLigne>) =>
    onChange(lignes.map((l) => (l.id === id ? { ...l, ...p } : l)));

  const remove = (id: string) => onChange(lignes.filter((l) => l.id !== id));

  const t = produitsTotals(lignes);

  return (
    <Card className="p-4 space-y-3 border-teal-500/25">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Boxes size={16} className="text-teal-400" strokeWidth={1.5} />
          <span className="text-slate-300 font-medium text-sm">Produits, décorations &amp; locations</span>
        </div>
        <div className="text-slate-500 text-xs flex-1">
          Photobooth, mascottes, feu de jour, wedding planner… Ajoutés à la proposition en plus des artistes.
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="bg-slate-950/60 border border-slate-800 rounded-md px-2.5 py-2 text-xs text-slate-200"
        >
          <option value="">Toutes catégories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2">
            <Search size={14} className="text-slate-500" strokeWidth={1.5} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un produit du catalogue…"
              className="flex-1 bg-transparent outline-none text-sm text-slate-200 placeholder:text-slate-600"
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-slate-500 hover:text-slate-300">
                <X size={14} strokeWidth={1.5} />
              </button>
            )}
          </div>
          {suggestions.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-slate-900 border border-slate-700 rounded-md shadow-xl max-h-72 overflow-auto">
              {suggestions.map((p) => (
                <button
                  key={p.id}
                  onClick={() => add(p)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-800 flex items-center gap-2 border-b border-slate-800/60 last:border-0"
                >
                  <Plus size={12} className="text-teal-400 shrink-0" strokeWidth={2} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-200 truncate">{p.libelle}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {p.categorie || '—'} · {eur(p.prixHT)} HT
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {search.trim() && suggestions.length === 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-500 italic">
              Aucun produit trouvé — ajoutez-le au catalogue dans Paramètres.
            </div>
          )}
        </div>
        <button
          onClick={addLibre}
          className="px-3 py-2 bg-slate-800/60 hover:bg-slate-800 border border-slate-700 text-slate-300 rounded-md text-xs font-medium flex items-center gap-1"
        >
          <Plus size={12} strokeWidth={1.5} /> Ligne libre
        </button>
      </div>

      {browse.length > 0 && !search.trim() && (
        <div className="flex flex-wrap gap-1.5">
          {browse.map((p) => (
            <button
              key={p.id}
              onClick={() => add(p)}
              className="px-2 py-1 rounded-md bg-slate-800/50 hover:bg-teal-500/20 border border-slate-700/60 hover:border-teal-500/40 text-[11px] text-slate-300"
            >
              + {p.libelle} · {eur(p.prixHT)}
            </button>
          ))}
        </div>
      )}

      {lignes.length === 0 ? (
        <p className="text-xs text-slate-500 italic py-2">Aucun produit ajouté à ce dossier.</p>
      ) : (
        <div className="space-y-2">
          {lignes.map((l) => (
            <div key={l.id} className="bg-slate-950/50 border border-slate-800/60 rounded-md p-3 space-y-2">
              <div className="flex items-start gap-2">
                <input
                  value={l.libelle}
                  onChange={(e) => patch(l.id, { libelle: e.target.value })}
                  placeholder="Libellé du produit"
                  className="flex-1 bg-slate-950/60 border border-slate-800 rounded-md px-2.5 py-1.5 text-xs text-slate-200 placeholder:text-slate-600"
                />
                <button onClick={() => remove(l.id)} className="p-1.5 text-slate-500 hover:text-red-400" title="Retirer">
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              </div>
              <textarea
                value={l.description}
                onChange={(e) => patch(l.id, { description: e.target.value })}
                rows={2}
                placeholder="Description reprise dans la proposition…"
                className="w-full bg-slate-950/60 border border-slate-800 rounded-md px-2.5 py-1.5 text-xs text-slate-300 placeholder:text-slate-600 resize-y whitespace-pre-wrap break-words"
              />
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">Qté</label>
                  <input
                    value={String(l.quantite)}
                    onChange={(e) => patch(l.id, { quantite: Number(e.target.value.replace(',', '.')) || 0 })}
                    inputMode="decimal"
                    className="w-full bg-slate-950/60 border border-slate-800 rounded-md px-2 py-1.5 text-xs text-slate-200"
                  />
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">Coût HT</label>
                  <input
                    value={String(l.coutHT)}
                    onChange={(e) => patch(l.id, { coutHT: Number(e.target.value.replace(',', '.')) || 0 })}
                    inputMode="decimal"
                    className="w-full bg-slate-950/60 border border-slate-800 rounded-md px-2 py-1.5 text-xs text-slate-200"
                  />
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-teal-400/80 mb-0.5">Vendu HT</label>
                  <input
                    value={String(l.prixHT)}
                    onChange={(e) => patch(l.id, { prixHT: Number(e.target.value.replace(',', '.')) || 0 })}
                    inputMode="decimal"
                    className="w-full bg-slate-950/60 border border-teal-700/50 rounded-md px-2 py-1.5 text-xs text-slate-100 font-medium"
                  />
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">Total HT</label>
                  <div className="px-2 py-1.5 text-xs text-slate-200 font-semibold">
                    {eur((Number(l.prixHT) || 0) * (Number(l.quantite) || 0))}
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-wrap gap-6 pt-2 border-t border-slate-800 text-xs">
            <div>
              <span className="text-slate-500">Coût produits&nbsp;: </span>
              <span className="text-slate-200">{eur(t.coutHT)}</span>
            </div>
            <div>
              <span className="text-slate-500">Vendu produits&nbsp;: </span>
              <span className="text-slate-100 font-semibold">{eur(t.venduHT)}</span>
            </div>
            <div>
              <span className="text-slate-500">Marge&nbsp;: </span>
              <span className={t.margeHT >= 0 ? 'text-emerald-400 font-semibold' : 'text-red-400 font-semibold'}>
                {eur(t.margeHT)}
              </span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
